import { useApi } from '@hero/context';
import type {
  UseFinancingAcceptOfferApi,
  UseFinancingAdminApplicationByIdApi,
  UseFinancingAdminApplicationsApi,
  UseFinancingAdminCancelApi,
  UseFinancingApplicationsApi,
  UseFinancingOfferLenderContactsApi,
  UseFinancingApproveRepaymentApi,
  UseFinancingCreateApplicationApi,
  UseFinancingLenderExpressInterestApi,
  UseFinancingLenderMarkFundedApi,
  UseFinancingLenderMarkRepaidApi,
  UseFinancingLenderRejectRepaymentApi,
  UseFinancingLenderSubmitOfferApi,
  UseFinancingLenderViewApi,
  UseFinancingMarkOutcomeApi,
  UseFinancingPartnerExpressInterestApi,
  UseFinancingPartnerGetApi,
  UseFinancingPartnerSubmitOfferApi,
  UseFinancingPartnersListApi,
  UseFinancingRejectRepaymentApi,
  UseFinancingSendToLendersApi,
  UseFinancingSubmitRepaymentReceiptApi,
  UseFinancingUpdateTagsApi,
  UseFinancingUploadInvoiceDocumentApi,
} from './types';
import { useInfiniteQueryAPI, useMutationAPI, useQueryAPI } from '../../utils';

/* ─────────────── carrier-side queries ─────────────── */

/**
 * Legacy alias for {@link useActiveLendersApi}. The backend
 * `/financing/partners` endpoint now delegates to
 * `LenderService.getActiveAPI()`, projecting each `Lender` doc to
 * the legacy {@link FinancingPartner} shape. New code should call
 * `useActiveLendersApi` directly.
 *
 * @deprecated
 * Hook API: {@link UseFinancingPartnersListApi}
 */
export const useFinancingPartnersListApi: UseFinancingPartnersListApi = (
  query,
  apiConfig,
) => {
  const api = useApi();
  return useQueryAPI({
    entity: 'financingPartner',
    apiFn: api.financing.getPartners,
    query,
    apiConfig,
  });
};

/**
 * Carrier "my applications" — paginated. Empty `query` returns the
 * full list; `query.status` filters. Visibility is enforced server-
 * side via `tenants[]`.
 *
 * Hook API: {@link UseFinancingApplicationsApi}
 */
export const useFinancingApplicationsApi: UseFinancingApplicationsApi = (
  query,
  apiConfig,
) => {
  const api = useApi();
  return useInfiniteQueryAPI({
    entity: 'financingApplication',
    apiFn: api.financing.getApplications,
    query,
    apiConfig,
  });
};

/**
 * Opted-in lender contacts for a specific offer row on an
 * application. Disabled until both `_id` and `offerId` are set.
 *
 * Hook API: {@link UseFinancingOfferLenderContactsApi}
 */
export const useFinancingOfferLenderContactsApi: UseFinancingOfferLenderContactsApi =
  (query, apiConfig) => {
    const api = useApi();
    return useQueryAPI({
      entity: 'financingApplication',
      apiFn: api.financing.getOfferLenderContacts,
      query,
      apiConfig,
      isEnabled: () => Boolean(query?._id) && Boolean(query?.offerId),
    });
  };

/* ─────────────── carrier-side mutations ─────────────── */

/**
 * Upload external invoice document. Returns an `ImageWithUpload`
 * that the caller stamps onto `invoice.document` of the subsequent
 * `useFinancingCreateApplicationApi` mutation.
 *
 * Hook API: {@link UseFinancingUploadInvoiceDocumentApi}
 */
export const useFinancingUploadInvoiceDocumentApi: UseFinancingUploadInvoiceDocumentApi =
  apiConfig => {
    const api = useApi();
    return useMutationAPI({
      entity: 'financingApplication',
      apiFn: api.financing.uploadInvoiceDocument,
      apiConfig,
    });
  };

/**
 * Create FinancingApplication (apply-form submit). Server snapshots
 * `Org.financingProfile` + emails invited lenders.
 *
 * Hook API: {@link UseFinancingCreateApplicationApi}
 */
export const useFinancingCreateApplicationApi: UseFinancingCreateApplicationApi =
  apiConfig => {
    const api = useApi();
    return useMutationAPI({
      entity: 'financingApplication',
      apiFn: api.financing.createApplication,
      apiConfig,
    });
  };

/**
 * Carrier accepts one of the approved offers.
 *
 * Hook API: {@link UseFinancingAcceptOfferApi}
 */
export const useFinancingAcceptOfferApi: UseFinancingAcceptOfferApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'financingApplication',
    apiFn: api.financing.acceptOffer,
    apiConfig,
  });
};

/**
 * Carrier uploads proof of repayment after funding. Stamps
 * `FinancingApplication.repaymentReceipt` and transitions the
 * application to `awaiting_repayment_approval`.
 *
 * Hook API: {@link UseFinancingSubmitRepaymentReceiptApi}
 */
export const useFinancingSubmitRepaymentReceiptApi: UseFinancingSubmitRepaymentReceiptApi =
  apiConfig => {
    const api = useApi();
    return useMutationAPI({
      entity: 'financingApplication',
      apiFn: api.financing.submitRepaymentReceipt,
      apiConfig,
    });
  };

/**
 * Admin approves a carrier-submitted repayment receipt. Flips
 * `awaiting_repayment_approval → repaid` and stamps
 * `outcome.repaidAt`.
 *
 * Hook API: {@link UseFinancingApproveRepaymentApi}
 */
export const useFinancingApproveRepaymentApi: UseFinancingApproveRepaymentApi =
  apiConfig => {
    const api = useApi();
    return useMutationAPI({
      entity: 'financingApplication',
      apiFn: api.financing.approveRepayment,
      apiConfig,
    });
  };

/**
 * Admin rejects a carrier-submitted repayment receipt. Reverts
 * `awaiting_repayment_approval → funded`, clears
 * `repaymentReceipt`, and records the reason in `outcome.notes`.
 *
 * Hook API: {@link UseFinancingRejectRepaymentApi}
 */
export const useFinancingRejectRepaymentApi: UseFinancingRejectRepaymentApi =
  apiConfig => {
    const api = useApi();
    return useMutationAPI({
      entity: 'financingApplication',
      apiFn: api.financing.rejectRepayment,
      apiConfig,
  });
};

/**
 * Super-admin: replace the `tags[]` array on an application
 * (`late` / `overdue` / `flagged`). Admin-curated today; a future
 * sweep cron will own the lifecycle tags automatically.
 *
 * Hook API: {@link UseFinancingUpdateTagsApi}
 */
export const useFinancingUpdateTagsApi: UseFinancingUpdateTagsApi =
  apiConfig => {
    const api = useApi();
    return useMutationAPI({
      entity: 'financingApplication',
      apiFn: api.financing.updateTags,
      apiConfig,
    });
  };

/* ─────────────── public partner-view (no auth) ─────────────── */

/**
 * Public partner-view of a single application. Disabled while the
 * `_id` or `p` URL params are missing so we don't burn a request
 * before React Router has parsed the URL.
 *
 * Hook API: {@link UseFinancingPartnerGetApi}
 */
export const useFinancingPartnerGetApi: UseFinancingPartnerGetApi = (
  query,
  apiConfig,
) => {
  const api = useApi();
  return useQueryAPI({
    entity: 'financingPartnerView',
    apiFn: api.financing.partnerGet,
    query,
    apiConfig,
    isEnabled: () => Boolean(query?._id) && Boolean(query?.p),
  });
};

/**
 * Partner clicks "I'm interested" — flips the offer's `decision`
 * from `'pending'` to `'interested'`, stamps `interestedAt`, and
 * unlocks the contact-reveal for subsequent reads.
 *
 * Hook API: {@link UseFinancingPartnerExpressInterestApi}
 */
export const useFinancingPartnerExpressInterestApi: UseFinancingPartnerExpressInterestApi =
  apiConfig => {
    const api = useApi();
    return useMutationAPI({
      entity: 'financingPartnerView',
      apiFn: api.financing.partnerExpressInterest,
      apiConfig,
    });
  };

/**
 * Partner submits or updates an offer from the partner-view.
 *
 * Hook API: {@link UseFinancingPartnerSubmitOfferApi}
 */
export const useFinancingPartnerSubmitOfferApi: UseFinancingPartnerSubmitOfferApi =
  apiConfig => {
    const api = useApi();
    return useMutationAPI({
      entity: 'financingPartnerView',
      apiFn: api.financing.partnerSubmitOffer,
      apiConfig,
    });
  };

/* ─────────────── public lender-view (single call) ─────────────── */

/**
 * Single-shot public lender-view payload — replaces the legacy
 * `useFinancingPartnerGetApi`. Disabled until both `_id` and `i`
 * are present in the URL so we don't fire a half-keyed request
 * before React Router has parsed the location.
 *
 * Hook API: {@link UseFinancingLenderViewApi}
 */
export const useFinancingLenderViewApi: UseFinancingLenderViewApi = (
  query,
  apiConfig,
) => {
  const api = useApi();
  return useQueryAPI({
    entity: 'financingPartnerView',
    apiFn: api.financing.lenderView,
    query,
    apiConfig,
    isEnabled: () => Boolean(query?._id) && Boolean(query?.i),
  });
};

/**
 * Lender clicks "I'm interested" from the public lender-view page.
 * Returns the same {@link FinancingLenderView} shape with
 * `contactRevealed: true`, so the SPA can mutate its TanStack Query
 * cache in place from a single response.
 *
 * Hook API: {@link UseFinancingLenderExpressInterestApi}
 */
export const useFinancingLenderExpressInterestApi: UseFinancingLenderExpressInterestApi =
  apiConfig => {
    const api = useApi();
    return useMutationAPI({
      entity: 'financingPartnerView',
      apiFn: api.financing.lenderExpressInterest,
      apiConfig,
    });
  };

/**
 * Lender submits or updates an offer from the public lender-view
 * page.
 *
 * Hook API: {@link UseFinancingLenderSubmitOfferApi}
 */
export const useFinancingLenderSubmitOfferApi: UseFinancingLenderSubmitOfferApi =
  apiConfig => {
    const api = useApi();
    return useMutationAPI({
      entity: 'financingPartnerView',
      apiFn: api.financing.lenderSubmitOffer,
      apiConfig,
    });
  };

/**
 * Lender stamps the operational funding event from the public
 * lender-view page once the carrier has accepted their offer. Returns
 * the refreshed {@link FinancingLenderView} so the SPA mutates its
 * TanStack Query cache in place — no second refetch needed.
 *
 * Hook API: {@link UseFinancingLenderMarkFundedApi}
 */
export const useFinancingLenderMarkFundedApi: UseFinancingLenderMarkFundedApi =
  apiConfig => {
    const api = useApi();
    return useMutationAPI({
      entity: 'financingPartnerView',
      apiFn: api.financing.lenderMarkFunded,
      apiConfig,
    });
  };

/**
 * Lender stamps the operational repayment event from the public
 * lender-view page once the carrier has settled the advance + fee.
 * Returns the refreshed {@link FinancingLenderView} so the SPA can
 * swap the repayment panel for the read-only "repaid on X" card in
 * place — no second refetch needed.
 *
 * Hook API: {@link UseFinancingLenderMarkRepaidApi}
 */
export const useFinancingLenderMarkRepaidApi: UseFinancingLenderMarkRepaidApi =
  apiConfig => {
    const api = useApi();
    return useMutationAPI({
      entity: 'financingPartnerView',
      apiFn: api.financing.lenderMarkRepaid,
      apiConfig,
    });
  };

/**
 * Lender rejects a carrier-submitted repayment receipt from the
 * public lender-view page. Returns the refreshed
 * {@link FinancingLenderView}; the SPA swaps the review mode for
 * the direct-record panel (status now `funded`) without a second
 * refetch.
 *
 * Hook API: {@link UseFinancingLenderRejectRepaymentApi}
 */
export const useFinancingLenderRejectRepaymentApi: UseFinancingLenderRejectRepaymentApi =
  apiConfig => {
    const api = useApi();
    return useMutationAPI({
      entity: 'financingPartnerView',
      apiFn: api.financing.lenderRejectRepayment,
      apiConfig,
    });
  };

/* ─────────────── admin-side ─────────────── */

/**
 * Admin: all applications with filters.
 *
 * Hook API: {@link UseFinancingAdminApplicationsApi}
 */
export const useFinancingAdminApplicationsApi: UseFinancingAdminApplicationsApi = (
  query,
  apiConfig,
) => {
  const api = useApi();
  return useInfiniteQueryAPI({
    entity: 'financingApplication',
    apiFn: api.financing.adminGetAll,
    query,
    apiConfig,
  });
};

/**
 * Admin: mark outcome (funded / repaid / defaulted).
 *
 * Hook API: {@link UseFinancingMarkOutcomeApi}
 */
export const useFinancingMarkOutcomeApi: UseFinancingMarkOutcomeApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'financingApplication',
    apiFn: api.financing.markOutcome,
    apiConfig,
  });
};

/**
 * Admin: flip a `pending_review` application to `submitted` and fan
 * the lender invites out. The button-row in the admin drawer is the
 * primary caller; the carrier dashboard does not know this exists.
 *
 * Hook API: {@link UseFinancingSendToLendersApi}
 */
export const useFinancingSendToLendersApi: UseFinancingSendToLendersApi =
  apiConfig => {
    const api = useApi();
    return useMutationAPI({
      entity: 'financingApplication',
      apiFn: api.financing.sendToLenders,
      apiConfig,
    });
  };

/**
 * Admin: cancel a `pending_review` or `submitted` application.
 *
 * Hook API: {@link UseFinancingAdminCancelApi}
 */
export const useFinancingAdminCancelApi: UseFinancingAdminCancelApi =
  apiConfig => {
    const api = useApi();
    return useMutationAPI({
      entity: 'financingApplication',
      apiFn: api.financing.adminCancel,
      apiConfig,
    });
  };

/**
 * Admin: read one application by id. The query is disabled until the
 * id is present so the drawer can mount without forcing a request
 * before the row is picked.
 *
 * Hook API: {@link UseFinancingAdminApplicationByIdApi}
 */
export const useFinancingAdminApplicationByIdApi: UseFinancingAdminApplicationByIdApi =
  (query, apiConfig) => {
    const api = useApi();
    return useQueryAPI({
      entity: 'financingApplication',
      apiFn: api.financing.adminGetOne,
      query,
      apiConfig,
      isEnabled: () => Boolean(query?._id),
    });
  };
